/**
 * Depots, and where each sits: which company owns its stock, which place
 * hosts it.
 *
 * The two selects save on change. That is the correction an import leaves
 * behind — a list of depots no company code prefixes — and making the
 * operator open a form per depot to fix forty of them would be the reason
 * they never get fixed. A depot with no company is flagged rather than
 * filtered away, and the screen can show only those.
 *
 * The ERP code saves the same way, when the field is left. It is what the
 * ERP's stock feed names a depot by; left empty, the feed matches on the
 * depot's own code, so an installation whose codes agree types nothing.
 *
 * Taking a depot out of service rather than deleting it stays the rule:
 * stock levels, customer scopes and orders point at a depot by code.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@hardauto/core-api";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  Input,
  Select,
  Toolbar,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import type { Company, PointOfSale, ReferentialApi, Site } from "../../api";
import { NAMESPACE } from "../../i18n";
import { DeleteDialog, type DeleteTarget } from "./DeleteDialog";
import { VisibilityControls } from "./VisibilityControls";

export interface DepotsTabProps {
  api: ReferentialApi;
  sites: readonly Site[];
  companies: readonly Company[];
  points: readonly PointOfSale[];
  loading: boolean;
  failed: boolean;
  reload: () => void;
  mayWrite: boolean;
  /** Open with only the depots that have no company — after an import. */
  initiallyUnattachedOnly?: boolean;
}

export function DepotsTab({
  api,
  sites,
  companies,
  points,
  loading,
  failed,
  reload,
  mayWrite,
  initiallyUnattachedOnly = false,
}: DepotsTabProps) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const [unattachedOnly, setUnattachedOnly] = useState(initiallyUnattachedOnly);
  const [deleting, setDeleting] = useState<DeleteTarget | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [point, setPoint] = useState("");
  const [erpCode, setErpCode] = useState("");

  const pointsByCode = useMemo(() => new Map(points.map((entry) => [entry.code, entry])), [points]);
  const companiesByCode = useMemo(
    () => new Map(companies.map((entry) => [entry.code, entry])),
    [companies],
  );
  const rows = unattachedOnly ? sites.filter((site) => !site.company_code) : sites;

  function attach(site: Site, body: { company_code?: string; point_of_sale_code?: string }) {
    void action.run(`attach:${site.code}`, async () => {
      try {
        await api.updateSite(site.code, body);
        reload();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  // Two depots cannot carry one ERP code — a stock line names its depot by
  // that code alone — and the API says so with a 409.
  function showFailure(error: unknown) {
    const taken = error instanceof ApiError && error.status === 409;
    toast.show(t(taken ? "structure.depots.erpCodeTaken" : "error.title"), { tone: "danger" });
  }

  // Choices offered: the active ones, plus whatever the depot already points
  // at even if it is inactive or unknown — a select that cannot show the
  // current value silently shows another one.
  function companyOptions(current: string) {
    const offered = companies.filter((entry) => entry.is_active || entry.code === current);
    const unknown = current && !companiesByCode.has(current) ? [current] : [];
    return (
      <>
        <option value="">{t("structure.depots.none")}</option>
        {offered.map((entry) => (
          <option key={entry.code} value={entry.code}>
            {entry.code} — {entry.name}
          </option>
        ))}
        {unknown.map((entry) => (
          <option key={entry} value={entry}>
            {entry}
          </option>
        ))}
      </>
    );
  }

  function pointOptions(current: string) {
    const offered = points.filter((entry) => entry.is_active || entry.code === current);
    const unknown = current && !pointsByCode.has(current) ? [current] : [];
    return (
      <>
        <option value="">{t("structure.depots.none")}</option>
        {offered.map((entry) => (
          <option key={entry.code} value={entry.code}>
            {entry.city ? `${entry.name} (${entry.city})` : entry.name}
          </option>
        ))}
        {unknown.map((entry) => (
          <option key={entry} value={entry}>
            {entry}
          </option>
        ))}
      </>
    );
  }

  const columns: Column<Site>[] = [
    {
      key: "code",
      header: t("structure.depots.column.code"),
      render: (row) => <code>{row.code}</code>,
    },
    { key: "name", header: t("structure.depots.column.name"), render: (row) => row.name },
    {
      key: "erpCode",
      header: t("structure.depots.column.erpCode"),
      render: (row) =>
        mayWrite ? (
          <ErpCodeInput
            // Keyed on the saved value so a reload shows what was stored.
            key={`${row.code}:${row.external_ref}`}
            label={t("structure.depots.erpCodeLabel", { code: row.code })}
            saved={row.external_ref}
            placeholder={row.code}
            onSave={async (value) => {
              try {
                await api.updateSite(row.code, { external_ref: value });
                reload();
                return true;
              } catch (error) {
                showFailure(error);
                return false;
              }
            }}
          />
        ) : row.external_ref ? (
          <code>{row.external_ref}</code>
        ) : (
          "—"
        ),
    },
    {
      key: "company",
      header: t("structure.depots.column.company"),
      render: (row) =>
        mayWrite ? (
          <div className="hardauto-structure__inline">
            <Select
              aria-label={t("structure.depots.companyLabel", { code: row.code })}
              value={row.company_code}
              disabled={action.isBusy(`attach:${row.code}`)}
              onChange={(event) => attach(row, { company_code: event.target.value })}
            >
              {companyOptions(row.company_code)}
            </Select>
            {row.company_code ? null : (
              <Badge tone="warning">{t("structure.depots.withoutCompany")}</Badge>
            )}
          </div>
        ) : row.company_code ? (
          (companiesByCode.get(row.company_code)?.name ?? row.company_code)
        ) : (
          <Badge tone="warning">{t("structure.depots.withoutCompany")}</Badge>
        ),
    },
    {
      key: "point",
      header: t("structure.depots.column.point"),
      render: (row) =>
        mayWrite ? (
          <Select
            aria-label={t("structure.depots.pointLabel", { code: row.code })}
            value={row.point_of_sale_code}
            disabled={action.isBusy(`attach:${row.code}`)}
            onChange={(event) => attach(row, { point_of_sale_code: event.target.value })}
          >
            {pointOptions(row.point_of_sale_code)}
          </Select>
        ) : (
          (pointsByCode.get(row.point_of_sale_code)?.name ?? (row.point_of_sale_code || "—"))
        ),
    },
    {
      key: "location",
      header: t("structure.depots.column.location"),
      // The place's city: a depot is where its point of sale is. The depot's
      // own city is the fallback for one not attached to a place yet.
      render: (row) => pointsByCode.get(row.point_of_sale_code)?.city || row.city || "—",
      secondary: true,
    },
    {
      key: "state",
      header: t("structure.depots.column.state"),
      render: (row) => (
        <div className="hardauto-structure__inline">
          <VisibilityControls
            customers={{
              visible: row.is_active,
              hiddenByParent: row.hidden_by_parent_customers ?? false,
            }}
            backOffice={{
              visible: row.back_office_visible,
              hiddenByParent: row.hidden_by_parent_back_office ?? false,
            }}
            parent="company-or-point"
            editable={mayWrite}
            isBusy={(audience) => action.isBusy(`toggle:${audience}:${row.code}`)}
            onToggle={(audience, visible) =>
              void action.run(`toggle:${audience}:${row.code}`, async () => {
                try {
                  await api.updateSite(
                    row.code,
                    audience === "customers"
                      ? { is_active: visible }
                      : { back_office_visible: visible },
                  );
                  reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              })
            }
          />
          {mayWrite ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setDeleting({
                    title: t("deletion.title.depot", { name: row.name, code: row.code }),
                    consequences: [t("deletion.will.stock")],
                    run: () => api.deleteSite(row.code),
                  })
                }
              >
                {t("deletion.action")}
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  const unattached = sites.filter((site) => !site.company_code).length;

  return (
    <div className="hardauto-structure__panel">
      <Toolbar>
        <Checkbox
          label={t("structure.depots.unattachedOnly", { count: unattached })}
          checked={unattachedOnly}
          onChange={(event) => setUnattachedOnly(event.target.checked)}
        />
        {mayWrite && !creating ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("structure.depots.create")}
          </Button>
        ) : null}
      </Toolbar>

      {creating ? (
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!code.trim() || !name.trim()) return;
            void action.run("create", async () => {
              try {
                await api.createSite({
                  code: code.trim(),
                  name: name.trim(),
                  company_code: company,
                  point_of_sale_code: point,
                  external_ref: erpCode.trim(),
                });
                setCode("");
                setName("");
                setCompany("");
                setPoint("");
                setErpCode("");
                setCreating(false);
                reload();
              } catch (error) {
                showFailure(error);
              }
            });
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.depots.column.code")} required>
              {(props) => (
                <Input {...props} value={code} onChange={(event) => setCode(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.depots.column.name")} required>
              {(props) => (
                <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.depots.column.erpCode")}>
              {(props) => (
                <Input
                  {...props}
                  value={erpCode}
                  placeholder={code.trim()}
                  onChange={(event) => setErpCode(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.depots.column.company")}>
              {(props) => (
                <Select
                  {...props}
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                >
                  {companyOptions(company)}
                </Select>
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.depots.column.point")}>
              {(props) => (
                <Select {...props} value={point} onChange={(event) => setPoint(event.target.value)}>
                  {pointOptions(point)}
                </Select>
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("create")}
              busyLabel={t("structure.save")}
            >
              {t("structure.create")}
            </Button>
            <Button onClick={() => setCreating(false)}>{t("structure.cancel")}</Button>
          </div>
        </form>
      ) : null}

      <DeleteDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={reload} />

      <DataTable<Site>
        caption={t("structure.depots.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.code}
        loading={loading}
        loadingLabel={t("structure.depots.loading")}
        error={
          failed
            ? { title: t("error.title"), onRetry: reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={
          unattachedOnly
            ? {
                title: t("structure.depots.allAttached.title"),
                description: t("structure.depots.allAttached.body"),
              }
            : {
                title: t("structure.depots.empty.title"),
                description: t("structure.depots.empty.body"),
              }
        }
      />
    </div>
  );
}

interface ErpCodeInputProps {
  label: string;
  saved: string;
  placeholder: string;
  /** Resolves `false` when the value was refused; the cell then shows `saved` again. */
  onSave: (value: string) => Promise<boolean>;
}

/** A text cell that saves when left, or on Enter, and only if it changed. */
function ErpCodeInput({ label, saved, placeholder, onSave }: ErpCodeInputProps) {
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  async function commit() {
    const next = value.trim();
    if (next === saved || busy) return;
    setBusy(true);
    const accepted = await onSave(next);
    setBusy(false);
    if (!accepted) setValue(saved);
  }
  return (
    <Input
      aria-label={label}
      value={value}
      placeholder={placeholder}
      disabled={busy}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void commit();
        }
      }}
    />
  );
}
